<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import CertView from '@/components/institution/CerView.vue'
import CareerView from '@/components/institution/CareerVie.vue'
import Configuration from '@/components/institution/Configuration.vue'
import { getInstitutionConfiguration, getInstitutionConfigurationTest, getInstitutionName } from '@/components/utils/functions'
// Store y datos
const store = useStore()
const idInstitution = computed(() => store.getters['global/currentInstitution'])

const institutionName = ref('')
const institutionConfiguration = ref<any>({})
const institutionConfigurationTest = ref<any>({})
const activeTab = ref('responsables')
const selectTab = (tab: string) => {
	if (activeTab.value === tab) return
	activeTab.value = tab
}

const campusInfo = computed(() => {
	const selectedInstitution = store.state.global.selectedInstitution
	if (!selectedInstitution?.idCampus) return null
	return Object.values(selectedInstitution.idCampus).filter(campus => campus !== null && campus !== undefined)
})
const responsablesInfo = computed(() => {
	const selectedInstitution = store.state.global.selectedInstitution
	const responsables = selectedInstitution?.Responsable
	if (!responsables) return []

	return Object.entries(responsables).map(([id, responsable]) => ({
		id,
		...(responsable as any)
	}))
})
const carreras = computed(() => store.state.global.selectedInstitution?.Carrera || [])

onMounted(async () => {
	institutionName.value = await getInstitutionName(idInstitution.value)
	institutionConfiguration.value = await getInstitutionConfiguration(idInstitution.value)
	institutionConfigurationTest.value = await getInstitutionConfigurationTest(idInstitution.value)
})
watch(idInstitution, async (newId, oldId) => {
	if (newId !== oldId) {
		institutionName.value = await getInstitutionName(idInstitution.value)
		institutionConfiguration.value = await getInstitutionConfiguration(idInstitution.value)
		institutionConfigurationTest.value = await getInstitutionConfigurationTest(idInstitution.value)
	}
})
</script>

<template>
	<div class="institution-page">
		<!-- Header -->
		<VaCard class="header-card" flat>
			<div class="header-content">
				<div class="header-title">
					<span class="institution-title">{{ institutionName }}</span>
				</div>
				<div class="campus-info">
					<VaAlert v-if="campusInfo && campusInfo.length > 0" v-for="(campus, index) in campusInfo" :key="index" icon="school" center class="campus-alert">
						{{ (campus as any).idCampus }} / {{ (campus as any).claveCampus ? (campus as any).claveCampus : 'Sin Clave' }} / {{ (campus as any).campus ? (campus as any).campus : 'Sin Campus' }} /
						{{ (campus as any).entidadFederativa ? (campus as any).entidadFederativa : 'Sin Entidad Federativa' }}
					</VaAlert>
					<VaAlert v-else color="warning" icon="favorite" center class="campus-alert"> No hay campus registrados </VaAlert>
				</div>
			</div>
		</VaCard>

		<!-- Tabs Navigation -->
		<div class="tabs-container">
			<VaTabs v-model="activeTab" class="custom-tabs">
				<VaTab label="Responsables" name="responsables" @click="selectTab('responsables')" />
				<VaTab label="Programas" name="programs" @click="selectTab('programs')" />
				<VaTab label="Configuración" name="configuration" @click="selectTab('configuration')" />
			</VaTabs>
		</div>

		<!-- Content Sections -->
		<VaCard v-if="activeTab === 'responsables'" class="content-section" flat>
			<div class="info-cards">
				<div class="info-col">
					<div class="info-header">
						<img class="section-icon" src="../assets/icons/certificateIcon.svg" alt="Certificados" />
						<strong class="info-title">Certificados</strong>
					</div>
					<div class="certificates-scroll">
						<CertView :responsables="responsablesInfo" type="certificados" />
					</div>
				</div>
				<div class="info-col">
					<div class="info-header">
						<img class="section-icon" src="../assets/icons/degreeIcon.svg" alt="Títulos" />
						<strong class="info-title">Títulos</strong>
					</div>
					<div class="certificates-scroll">
						<CertView :responsables="responsablesInfo" type="titulos" />
					</div>
				</div>
			</div>
		</VaCard>

		<VaCard v-if="activeTab === 'programs'" class="content-section" flat>
			<CareerView :carreras="carreras" />
		</VaCard>

		<VaCard v-if="activeTab === 'configuration'" class="content-section" flat>
			<Configuration :configuration="institutionConfiguration" :configuration-test="institutionConfigurationTest" />
		</VaCard>
	</div>
</template>

<style scoped>
.institution-page {
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	min-height: 100vh;
	padding: 2rem;
	border-radius: 0;
}

.header-card {
	margin-bottom: 2rem;
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.2);
	padding: 2rem;
}

.header-content {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.header-title {
	text-align: center;
}

.institution-title {
	font-size: 2.5rem;
	font-weight: 700;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.campus-info {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.campus-alert {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	border: none;
	border-radius: 12px;
	box-shadow: 0 4px 15px rgba(107, 176, 255, 0.3);
	color: white;
	font-weight: 500;
}

.tabs-container {
	margin-bottom: 2rem;
}

.custom-tabs {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.2);
	overflow: hidden;
}

.content-section {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.2);
	padding: 2rem;
}

.info-cards {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 3rem;
}

.info-col {
	display: flex;
	flex-direction: column;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 16px;
	padding: 2rem;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.3);
	transition:
		transform 0.3s ease,
		box-shadow 0.3s ease;
}

.info-col:hover {
	transform: translateY(-5px);
	box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.info-header {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid rgba(107, 176, 255, 0.2);
}

.section-icon {
	width: 80px;
	height: 80px;
	margin-bottom: 1rem;
	filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
	transition: transform 0.3s ease;
}

.info-col:hover .section-icon {
	transform: scale(1.05);
}

.info-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #0873c4;
	text-align: center;
	text-transform: uppercase;
	letter-spacing: 1px;
}

.certificates-scroll {
	min-height: 280px;
	max-height: 65vh;
	overflow-y: auto;
	width: 100%;
	padding-right: 8px;
	padding-bottom: 1rem;
	border-radius: 8px;
	margin-bottom: 1rem;
}

.certificates-scroll::-webkit-scrollbar {
	width: 8px;
}

.certificates-scroll::-webkit-scrollbar-track {
	background: rgba(0, 0, 0, 0.05);
	border-radius: 4px;
}

.certificates-scroll::-webkit-scrollbar-thumb {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	border-radius: 4px;
}

.certificates-scroll::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
}

/* Responsive Design */
@media (max-width: 1200px) {
	.institution-page {
		padding: 1.5rem;
	}

	.info-cards {
		gap: 2rem;
	}
}

@media (max-width: 900px) {
	.institution-page {
		padding: 1rem;
	}

	.header-card {
		padding: 1.5rem;
	}

	.institution-title {
		font-size: 2rem;
	}

	.info-cards {
		grid-template-columns: 1fr;
		gap: 2rem;
	}

	.info-col {
		padding: 1.5rem;
	}

	.content-section {
		padding: 1.5rem;
	}
}

@media (max-width: 600px) {
	.institution-page {
		padding: 0.5rem;
	}

	.header-card {
		padding: 1rem;
		margin-bottom: 1rem;
	}

	.institution-title {
		font-size: 1.5rem;
	}

	.info-col {
		padding: 1rem;
	}

	.content-section {
		padding: 1rem;
	}

	.section-icon {
		width: 60px;
		height: 60px;
	}

	.info-title {
		font-size: 1rem;
	}
}
</style>
